import { create } from 'zustand';
import api from '@/features/api/axios';
import { logger } from '@/features/help/utils/logger';
import { withImageTimeout } from '../utils/timeout.utils';

// Track which logo URLs have already been preloaded
const preloadedLogoUrls = new Set<string>();

interface LogoStore {
  logoUrl: string | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  setLogoUrl: (url: string | null) => void;
  fetchLogo: () => Promise<void>;
  preloadLogoImage: () => Promise<boolean>;
  ensureFullUrl: (url: string) => string;
  clearLogo: () => void;
}

export const useLogoStore = create<LogoStore>((set, get) => ({
  logoUrl: null,
  isLoading: false,
  error: null,

  /**
   * Ensure URL has the proper prefix
   */
  ensureFullUrl: (url) => {
    // If it's already a full URL (starts with http or data:), return as is
    if (!url || url.startsWith('http') || url.startsWith('data:')) {
      return url;
    }

    // If it's a relative URL, prepend the API URL
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';

    // Ensure we don't double-prefix with /api
    if (url.startsWith('/api/')) {
      return `${apiUrl}${url}`;
    }

    // Otherwise prepend /api/ if needed
    if (!url.startsWith('/')) {
      return `${apiUrl}/api/${url}`;
    }

    return `${apiUrl}/api${url}`;
  },

  setLogoUrl: (url) => {
    // If no URL, clear the logo
    if (!url) {
      set({
        logoUrl: null,
        error: null,
      });
      logger.debug('Logo Store', 'Logo URL cleared', {
        previousUrl: get().logoUrl,
      });
      return;
    }

    // Ensure URL is properly formatted
    const fullUrl = get().ensureFullUrl(url);

    logger.debug('Logo Store', 'Setting logo URL', {
      original: url,
      fullUrl,
      timestamp: new Date().toISOString(),
    });

    set({
      logoUrl: fullUrl,
      error: null,
    });
  },

  fetchLogo: async () => {
    // If we already have a logo URL, no need to fetch again
    if (get().logoUrl) {
      logger.debug('Logo Store', 'Logo already exists, skipping fetch', {
        existingLogoUrl: get().logoUrl,
        timestamp: new Date().toISOString(),
      });
      return;
    }

    try {
      set({ isLoading: true, error: null });

      logger.debug('Logo Store', 'Fetching logo from API', {
        timestamp: new Date().toISOString(),
      });

      const { data: logo } = await api.get('/api/logo/logo');

      logger.debug('Logo Store', 'Logo API response received', {
        responseData: logo,
        hasFileUrl: !!logo?.file_url,
        timestamp: new Date().toISOString(),
      });

      // Check if we got a "No logo found" message
      if (logo?.code === 'LOGO_NOT_FOUND') {
        logger.info('Logo Store', 'No logo found for this user', {
          currentLogoUrl: get().logoUrl,
          timestamp: new Date().toISOString(),
        });
        // Clear any existing logo URL
        get().setLogoUrl(null);
        set({ isLoading: false });
        return;
      }

      if (logo?.file_url) {
        // Ensure file_url has proper URL format
        const fileUrl = get().ensureFullUrl(logo.file_url);
        const signedUrl = logo.signedUrl ? get().ensureFullUrl(logo.signedUrl) : null;

        // Prefer the signed URL for display if available
        const displayUrl = signedUrl || fileUrl;

        logger.debug('Logo Store', 'Setting logo URL from API response', {
          displayUrl,
          signedUrl,
          fileUrl,
          originalFileUrl: logo.file_url,
          originalSignedUrl: logo.signedUrl,
          timestamp: new Date().toISOString(),
        });

        // Set the logo URL
        get().setLogoUrl(displayUrl);

        logger.info('Logo Store', 'Logo fetch completed successfully', {
          newLogoUrl: displayUrl,
          timestamp: new Date().toISOString(),
        });
      } else {
        logger.warn('Logo Store', 'Logo API response missing file URL', {
          currentLogoUrl: get().logoUrl,
          responseData: logo,
          timestamp: new Date().toISOString(),
        });
      }

      set({ isLoading: false });
    } catch (error) {
      // Handle network errors gracefully
      const isNetworkError =
        error instanceof Error &&
        (error.message === 'Network Error' || error.message.includes('Connection refused'));

      logger.error('Logo Store', 'Failed to fetch logo', {
        error,
        isNetworkError,
        currentLogoUrl: get().logoUrl,
        stack: error instanceof Error ? error.stack : undefined,
        timestamp: new Date().toISOString(),
      });

      // If we have a network error, set a placeholder logo
      if (isNetworkError && !get().logoUrl) {
        logger.debug('Logo Store', 'Setting placeholder logo after network error', {
          timestamp: new Date().toISOString(),
        });
        const placeholderLogo =
          'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2YyZjJmMiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIGZpbGw9IiM5OTkiPkxvZ28gTm90IEF2YWlsYWJsZTwvdGV4dD48L3N2Zz4=';
        get().setLogoUrl(placeholderLogo);
      }

      set({
        isLoading: false,
        error: error instanceof Error ? error.message : String(error),
      });

      // Don't throw the error - this allows the app to continue even if logo fetch fails
      return;
    }
  },

  /**
   * Preloads the logo image after it's been fetched
   * Returns true if preloading was successful, false otherwise
   */
  preloadLogoImage: async () => {
    const logoUrl = get().logoUrl;

    if (!logoUrl) {
      logger.debug('Logo Store', 'No logo URL available to preload', {});
      return false;
    }

    // If we've already preloaded this URL, don't do it again
    if (preloadedLogoUrls.has(logoUrl)) {
      logger.debug('Logo Store', 'Logo was already preloaded, skipping', { logoUrl });
      return true;
    }

    try {
      // Simple preloading - create an image and wait for it to load
      logger.debug('Logo Store', 'Preloading logo image', { logoUrl });

      // Create a new Promise for the image load with timeout using utility
      await withImageTimeout(
        new Promise<void>((resolve, reject) => {
          const img = new Image();

          img.onload = () => {
            logger.success('Logo Store', 'Logo image preloaded successfully', { logoUrl });
            preloadedLogoUrls.add(logoUrl);
            resolve();
          };

          img.onerror = (error) => {
            logger.error('Logo Store', 'Failed to preload logo image', {
              logoUrl,
              error: error,
            });
            reject(new Error(`Failed to preload image: ${String(error)}`));
          };

          // If the image is already in the browser cache, it might already be complete
          img.src = logoUrl;
          if (img.complete) {
            logger.success('Logo Store', 'Logo image was already cached by browser', {
              logoUrl,
            });
            preloadedLogoUrls.add(logoUrl);
            resolve();
          }
        })
      );

      return true;
    } catch (error) {
      logger.error('Logo Store', 'Error during logo preload', {
        logoUrl,
        error,
      });
      return false;
    }
  },

  clearLogo: () => {
    set({
      logoUrl: null,
      isLoading: false,
      error: null,
    });
    logger.debug('Logo Store', 'Logo store cleared');
  },
}));
